import { useCallback } from 'react';
import type { LeafPaths } from '@utils/types';
import { get } from 'lodash-es';

import language from './language';

export type NodesPaths<T, K extends string = ''> = T extends PropertyKey
  ? K
  : keyof T extends T
    ? K
    : {
        [P in keyof T]: P extends string
          ? K extends ''
            ? NodesPaths<T[P], `${P}`>
            : K | NodesPaths<T[P], `${K}.${P}`>
          : never;
      }[keyof T];

type LanguageSource = LeafPaths<typeof language>;
type LanguagePrefix = NodesPaths<typeof language>;

interface UseLanguageOptions {
  prefix: LanguagePrefix;
}

function useLanguage(): {
  getContent: (key: LanguageSource) => string;
};

function useLanguage({ prefix }: UseLanguageOptions): {
  getContent: (key: LeafPaths<typeof prefix>) => string;
};

function useLanguage(options?: UseLanguageOptions): {
  getContent: (key: string) => string;
} {
  const { prefix } = options ?? {};

  const getContent = useCallback(
    (key: string) => {
      const path = prefix ? `${prefix}.${key}` : key;
      return get(language, path) || key;
    },
    [prefix],
  );

  return {
    getContent,
  };
}

export { useLanguage };
